// IVR call reporting -- call volume by day/week/month, by hour of day, outcome
// breakdown (booked / transferred / abandoned), and IVR share of total
// bookings. Colors are the dataviz skill's validated dark categorical palette
// (references/palette.md), not reused ad hoc from other charts in this
// codebase, so they clear the CVD/contrast gates against this dark surface.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, PhoneCall, PhoneForwarded, PhoneOff, CheckCircle2, Timer, ChevronLeft, ChevronRight, Percent, UserRound } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { format } from "date-fns";
import {
  IvrCallLog,
  Granularity,
  fetchIvrCallLogs,
  fetchJobsCountForRange,
  callVolumeByGranularity,
  callVolumeByHour,
  outcomeCounts,
  outcomeSummary,
  callDurationSeconds,
  formatDuration,
  periodRange,
  shiftAnchor,
  anchorBucketKey,
  parseBucketKey,
} from "@/lib/ivrCallLogs";

// Dataviz skill dark categorical slots 1-6, in fixed order (outcomeCounts
// now returns 6 buckets since a transfer no longer swallows whether a
// booking also happened -- see ivrCallLogs.ts).
const SERIES_1_BLUE = "#3987e5";
const SERIES_2_ORANGE = "#d95926";
const SERIES_3_AQUA = "#199e70";
const SERIES_4_YELLOW = "#c98500";
const SERIES_5_MAGENTA = "#d55181";
const SERIES_6_GREEN = "#008300";
const OUTCOME_COLORS = [SERIES_1_BLUE, SERIES_2_ORANGE, SERIES_3_AQUA, SERIES_4_YELLOW, SERIES_5_MAGENTA, SERIES_6_GREEN];
const SHARE_COLORS = [SERIES_1_BLUE, SERIES_3_AQUA];

const CHART_GRID = "#2c2c2a";
const CHART_AXIS = "#383835";
const CHART_MUTED = "#898781";
const CHART_SURFACE = "#1a1a19";

const tooltipStyle = {
  background: CHART_SURFACE,
  border: `1px solid ${CHART_GRID}`,
  borderRadius: 8,
  color: "#ffffff",
  fontSize: 12,
};

const StatTile: React.FC<{ label: string; value: string; icon: React.ElementType; sub?: string }> = ({ label, value, icon: Icon, sub }) => (
  <div className="rounded-lg border border-border bg-background/40 p-3">
    <div className="flex items-center gap-2 text-muted-foreground text-xs">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <p className="text-2xl font-semibold text-foreground mt-1">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const dateInputValue = (d: Date) => format(d, "yyyy-MM-dd");

const IvrCallReportingTab: React.FC = () => {
  const [calls, setCalls] = useState<IvrCallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState<Granularity>("day");
  const [anchor, setAnchor] = useState<Date>(new Date());
  const [totalJobs, setTotalJobs] = useState<number | null>(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const didInitAnchor = useRef(false);

  const load = async () => {
    setLoading(true);
    setCalls(await fetchIvrCallLogs());
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  // Once, after the first successful load, snap the default view to the most
  // recent day/period that actually has a call -- more useful than "today",
  // which is often still empty when the office opens this screen.
  useEffect(() => {
    if (didInitAnchor.current || loading || calls.length === 0) return;
    didInitAnchor.current = true;
    const latest = calls.reduce((max, c) => Math.max(max, new Date(c.started_at).getTime()), 0);
    setAnchor(new Date(latest));
  }, [loading, calls]);

  const range = useMemo(() => periodRange(granularity, anchor), [granularity, anchor]);

  useEffect(() => {
    let cancelled = false;
    setJobsLoading(true);
    fetchJobsCountForRange(range.start, range.end).then((count) => {
      if (!cancelled) {
        setTotalJobs(count);
        setJobsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [range.start, range.end]);

  const periodCalls = useMemo(
    () => calls.filter((c) => {
      const t = new Date(c.started_at).getTime();
      return t >= range.start.getTime() && t < range.end.getTime();
    }),
    [calls, range]
  );

  const volume = useMemo(() => callVolumeByGranularity(calls, granularity), [calls, granularity]);
  const selectedKey = useMemo(() => anchorBucketKey(granularity, anchor), [granularity, anchor]);
  const byHour = useMemo(() => callVolumeByHour(periodCalls), [periodCalls]);
  const outcomes = useMemo(() => outcomeCounts(periodCalls), [periodCalls]);
  const summary = useMemo(() => outcomeSummary(periodCalls), [periodCalls]);

  const totalCalls = periodCalls.length;
  const avgDurationSeconds = totalCalls > 0 ? Math.round(periodCalls.reduce((sum, c) => sum + callDurationSeconds(c), 0) / totalCalls) : 0;
  const abandoned = outcomes.find((o) => o.outcome === "abandoned");
  const pct = (n: number) => (totalCalls > 0 ? Math.round((n / totalCalls) * 100) : 0);

  const ivrBookedCount = periodCalls.reduce((sum, c) => sum + (c.bookings_completed || 0), 0);
  const otherBookedCount = totalJobs !== null ? Math.max(0, totalJobs - ivrBookedCount) : null;
  const ivrSharePct = totalJobs && totalJobs > 0 ? Math.round((ivrBookedCount / totalJobs) * 100) : null;
  const shareData =
    totalJobs !== null
      ? [
          { key: "ivr", label: "Booked via IVR", count: ivrBookedCount },
          { key: "other", label: "Dispatcher / Other Channels", count: otherBookedCount ?? 0 },
        ]
      : [];

  const jumpToDate = (value: string) => {
    if (!value) return;
    const [y, m, d] = value.split("-").map(Number);
    if (!y || !m || !d) return;
    setAnchor(new Date(y, m - 1, d));
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl text-primary">Reporting</CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
          <div className="flex gap-1">
            {GRANULARITY_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={granularity === opt.value ? "secondary" : "outline"}
                onClick={() => setGranularity(opt.value)}
                className="h-7 px-2.5 text-xs"
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setAnchor((a) => shiftAnchor(granularity, a, -1))}>
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-sm font-medium text-foreground min-w-[10rem] text-center">{range.label}</span>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setAnchor((a) => shiftAnchor(granularity, a, 1))}>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
            <Input
              type="date"
              value={dateInputValue(anchor)}
              onChange={(e) => jumpToDate(e.target.value)}
              className="dark-input h-7 w-[9.5rem] text-xs"
            />
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : calls.length === 0 ? (
          <p className="text-sm text-muted-foreground">No calls logged yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <StatTile label="Total Calls" value={String(totalCalls)} icon={PhoneCall} />
              <StatTile
                label="Completed Booking"
                value={`${pct(summary.totalBooked)}%`}
                icon={CheckCircle2}
                sub={`${summary.totalBooked} calls${summary.transferredButBooked > 0 ? ` (${summary.transferredButBooked} also transferred)` : ""}`}
              />
              <StatTile
                label="Transferred"
                value={`${pct(summary.totalTransferred)}%`}
                icon={PhoneForwarded}
                sub={`${summary.totalTransferred} calls`}
              />
              <StatTile
                label="Asked for Operator Immediately"
                value={`${pct(summary.transferredImmediate)}%`}
                icon={UserRound}
                sub={`${summary.transferredImmediate} calls -- no booking attempt`}
              />
              <StatTile label="Abandoned" value={`${abandoned?.pct ?? 0}%`} icon={PhoneOff} sub={`${abandoned?.count ?? 0} calls`} />
              <StatTile label="Avg Duration" value={formatDuration(avgDurationSeconds)} icon={Timer} />
            </div>

            <div>
              <p className="text-sm font-semibold text-foreground mb-2">IVR Share of Total Bookings</p>
              <p className="text-xs text-muted-foreground mb-2">
                Compares bookings the IVR completed against every job in the system for the same period (dispatcher-entered,
                forward bookings, everything). taxicaller_data has no "booking placed" timestamp yet, so this is matched by
                the trip's scheduled pickup date -- solid for same-day/ASAP comparisons, less exact for bookings placed far
                ahead of their pickup day.
              </p>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <StatTile label="Total Bookings" value={totalJobs === null ? (jobsLoading ? "…" : "0") : String(totalJobs)} icon={PhoneCall} />
                <StatTile
                  label="Booked via IVR"
                  value={ivrSharePct === null ? "…" : `${ivrSharePct}%`}
                  icon={Percent}
                  sub={`${ivrBookedCount} bookings`}
                />
                <StatTile
                  label="Dispatcher / Other"
                  value={ivrSharePct === null ? "…" : `${100 - ivrSharePct}%`}
                  icon={Percent}
                  sub={otherBookedCount === null ? undefined : `${otherBookedCount} bookings`}
                />
              </div>
              {shareData.length > 0 && (
                <ResponsiveContainer width="100%" height={110}>
                  <BarChart data={shareData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                    <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" stroke={CHART_MUTED} fontSize={11} tickLine={false} axisLine={{ stroke: CHART_AXIS }} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" stroke={CHART_MUTED} fontSize={11} width={170} tickLine={false} axisLine={{ stroke: CHART_AXIS }} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={28}>
                      {shareData.map((entry, i) => (
                        <Cell key={entry.key} fill={SHARE_COLORS[i % SHARE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-foreground">Call Volume</p>
                <p className="text-xs text-muted-foreground">Click a bar to jump to that period</p>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={volume} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" stroke={CHART_MUTED} fontSize={11} tickLine={false} axisLine={{ stroke: CHART_AXIS }} />
                  <YAxis stroke={CHART_MUTED} fontSize={11} tickLine={false} axisLine={{ stroke: CHART_AXIS }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar
                    dataKey="count"
                    name="Calls"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                    cursor="pointer"
                    onClick={(data: unknown) => {
                      const point = data as { key?: string; payload?: { key?: string } } | undefined;
                      const key = point?.key ?? point?.payload?.key;
                      if (key) setAnchor(parseBucketKey(granularity, key));
                    }}
                  >
                    {volume.map((entry) => (
                      <Cell key={entry.key} fill={entry.key === selectedKey ? SERIES_2_ORANGE : SERIES_1_BLUE} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Calls by Hour of Day</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={byHour} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" stroke={CHART_MUTED} fontSize={10} interval={1} tickLine={false} axisLine={{ stroke: CHART_AXIS }} />
                  <YAxis stroke={CHART_MUTED} fontSize={11} tickLine={false} axisLine={{ stroke: CHART_AXIS }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="count" name="Calls" fill={SERIES_1_BLUE} radius={[4, 4, 0, 0]} maxBarSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Call Outcomes</p>
              <p className="text-xs text-muted-foreground mb-2">
                No explicit "abandoned"/"transferred" flag exists in the data -- outcomes are inferred. A transfer no
                longer hides whether a booking also happened: "Transferred (Booking Completed)" is a genuinely
                different, better outcome than a transfer with nothing attempted at all, so they're split out below
                rather than lumped into one "Transferred" bucket. These six are mutually exclusive and sum to 100% --
                the stat tiles above use overlapping counts instead (a transferred-and-booked call counts in both
                "Completed Booking" and "Transferred").
              </p>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={outcomes} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" stroke={CHART_MUTED} fontSize={11} tickLine={false} axisLine={{ stroke: CHART_AXIS }} allowDecimals={false} />
                  <YAxis type="category" dataKey="label" stroke={CHART_MUTED} fontSize={11} width={210} tickLine={false} axisLine={{ stroke: CHART_AXIS }} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    cursor={{ fill: "rgba(255,255,255,0.04)" }}
                    formatter={(value: number, _name, item) => [`${value} calls (${item.payload.pct}%)`, "Count"]}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={28}>
                    {outcomes.map((entry, i) => (
                      <Cell key={entry.outcome} fill={OUTCOME_COLORS[i % OUTCOME_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default IvrCallReportingTab;
